import React, { useEffect, useRef, useState } from 'react';
import { Empty, Image } from 'antd';

// Viewing links expire after an hour. The first failed image refreshes the
// links once. Afterwards, an image is unavailable if it fails again or if
// the refresh didn't replace its link (the refresh failed or found nothing).
const RequestPhotoGallery = ({ imageUrls = [], onRefresh, emptyText = 'No images uploaded', imageStyle }) => {
  // Refs, not state: several images can fail before the next render.
  const refresh = useRef('idle');
  const failedUrls = useRef(new Set());
  const [refreshFinished, setRefreshFinished] = useState(false);
  const [unavailable, setUnavailable] = useState(() => new Set());

  useEffect(() => {
    if (!refreshFinished) return;
    const stale = imageUrls.filter((url) => failedUrls.current.has(url));
    if (stale.length) {
      setUnavailable((current) => new Set([...current, ...stale]));
    }
  }, [refreshFinished, imageUrls]);

  if (imageUrls.length === 0) {
    return <Empty description={emptyText} style={{ margin: '16px 0' }} />;
  }

  const handleError = async (url) => {
    failedUrls.current.add(url);
    if (refresh.current === 'refreshing') return;
    if (refresh.current === 'idle' && onRefresh) {
      refresh.current = 'refreshing';
      try {
        await onRefresh();
      } catch (error) {
        // Handled below: links that weren't replaced become unavailable.
      } finally {
        refresh.current = 'done';
        setRefreshFinished(true);
      }
      return;
    }
    setUnavailable((current) => new Set(current).add(url));
  };

  return (
    <div className="image-grid">
      {imageUrls.map((url, index) => (unavailable.has(url) ? (
        <div key={`unavailable-${url}`} className="request-photo-unavailable" style={{ padding: 16, textAlign: 'center', background: '#fafafa' }}>
          Image unavailable
        </div>
      ) : (
        <Image
          key={url}
          src={url}
          alt={`Request image ${index + 1}`}
          onError={() => handleError(url)}
          style={{ width: '100%', marginBottom: 8, objectFit: 'contain', ...imageStyle }}
        />
      )))}
    </div>
  );
};

export default RequestPhotoGallery;
